// THE `plan_authored` PAYLOAD — the task envelopes a planning run authored, carried
// whole so the mothership mints them without re-deriving anything.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { SETUP_ASK_WIRE_MAX } from '../setup-ask';
import { TaskEnvelopeSchema } from '../task-envelope';

export const PlanAuthoredTaskSchema = z.object({
  id: z.string().min(1),
  depends_on: z.array(z.string().min(1)).default([]),
  envelope: TaskEnvelopeSchema,
  /**
   * The same-area collision gate's opt-out (`tasks.parallel_safe`, #513) —
   * never model-declared. Today's box always sends `false` (`AuthoredTask`,
   * `apps/runner/src/modes/scout/base.ts`), and the mothership ignores the wire
   * value anyway: `ingestScoutPlanTasks` (`apps/worker`) clamps it to `false`.
   * Defaults false for a producer that predates the field.
   */
  parallel_safe: z.boolean().default(false),
  /**
   * A SETUP pass's one-time ask declares its kind here (#7169), and the ingest files it
   * under the repository's permanent `setupAskTaskDbId` (`@developerz/db`) instead of the
   * run-scoped id, so a declined ask is not filed again. An OPEN bounded string on purpose: the closed set
   * (`SETUP_ASK_KINDS`) is checked per task at the ingest seam, because an enum here would
   * drop the whole plan over one unknown kind (`../setup-ask.ts` § ROLLOUT). Absent for
   * every other task, and from a box that predates the field.
   */
  setup_ask: z.string().min(1).max(SETUP_ASK_WIRE_MAX).optional(),
});
export type PlanAuthoredTask = z.infer<typeof PlanAuthoredTaskSchema>;

export const PlanAuthoredPayloadSchema = z
  .object({
    event: z.literal('plan_authored'),
    count: z.number().int().nonnegative().optional(),
    reason: z.string().optional(),
    tasks: z.array(PlanAuthoredTaskSchema),
  })
  .superRefine((payload, ctx) => {
    // Cross-check the DAG at the wire boundary: every `depends_on` must name a
    // sibling task id in the SAME payload. A dangling ref maps through
    // `authoredTaskId` to a row id that never exists, enqueuing a task that can
    // never become ready (`claimNextReady` waits forever). Scout authoring
    // already gates this (`PlanGraph.validate`), but the wire contract must too —
    // a malformed `plan_authored` must be rejected here, never enqueued.
    //
    // UNIQUENESS is checked first, because the closure check below is a SET
    // membership test and a duplicate id clears it silently. The consumer hashes
    // the local id into the row id (`authoredTaskId(runId, t.id)`), so two tasks
    // sharing one id mint one row: `createTaskIfAbsent` answers null for the
    // second, no audit row is written for it, and its `plan_groups` row is eaten
    // by `onConflictDoNothing` — the plan reports ingested with a slice of the
    // work simply absent. A duplicate also makes every `depends_on` naming that
    // id ambiguous, so there is no recovery downstream either.
    const seen = new Set<string>();
    payload.tasks.forEach((task, i) => {
      if (seen.has(task.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate task id '${task.id}' — ids must be unique within one plan`,
          path: ['tasks', i, 'id'],
        });
      }
      seen.add(task.id);
    });
    const ids = new Set(payload.tasks.map((t) => t.id));
    payload.tasks.forEach((task, i) => {
      task.depends_on.forEach((dep, j) => {
        if (!ids.has(dep)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `task '${task.id}' depends on unknown task '${dep}'`,
            path: ['tasks', i, 'depends_on', j],
          });
        }
      });
    });
  });
